import React, {createRef, ReactElement, RefObject} from 'react';
import MComponent from '../Toolkit/Components/Component';
import {Column} from 'primereact/column';
import MTable, {MTableFilters} from '../Toolkit/Components/Controls/Table';
import MButton from '../Toolkit/Components/Controls/Button';
import MToolbar from '../Toolkit/Components/Controls/Toolbar';
import {MNewIconName} from '../Toolkit/Icons';
import ContactDialog, {ContactData} from '../Dialogs/ContactDialog';
import NSwagAdapter from '../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../Toolkit/Api/NSwagClientConfiguration';
import TrafficManagementAccessTokenProvider from '../Api/TrafficManagementAccessTokenProvider';
import {CreateCrewContactRequest, CrewContactClient} from '../Api/CrewContactClient';
import {IMsalContext} from '@azure/msal-react';
import {Contact, ContactMultiReply, ContactReply, ContactDeleteReply} from '../Api/ControllerTypes/ContactTypes';
import MSpacer from '../Toolkit/Components/Controls/Spacer';
import {CrewClient, UpdateCrewRequest} from '../Api/CrewClient';
import {CrewData, CrewDataReply } from '../Api/ControllerTypes/CrewTypes';
import {MNullable} from '../Toolkit/Utils';
import EnumContext from '../Context/enum-context';

type ContactRow = {
    data: Contact;
}

type Props = {
    parentId: number;
    msalContext: IMsalContext;
}

/** */
type State = {
    filters: MTableFilters;
    rowData: ContactRow[];
    crewData: MNullable<CrewData>;
    selectedRow?: ContactRow;
}

class ContactListControl extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>;

    private readonly m_crewClient: NSwagAdapter<CrewClient>;
    private readonly m_contactClient: NSwagAdapter<CrewContactClient>;
    private readonly m_editRef: RefObject<ContactDialog>;

    public constructor(props: Props) {
        super(props);

        this.m_editRef = createRef<ContactDialog>();
        this.m_crewClient = new NSwagAdapter<CrewClient>(new CrewClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_contactClient = new NSwagAdapter<CrewContactClient>(new CrewContactClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));

        this.bindMethods();
        this.initializeState();
    }

    private bindMethods(): void {
        this.new_Click = this.new_Click.bind(this);
        this.editComplete = this.editComplete.bind(this);
        this.makePrimary_Click = this.makePrimary_Click.bind(this);
        this.delete_Click = this.delete_Click.bind(this);
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.isNoRowSelected = this.isNoRowSelected.bind(this);
        this.primaryTemplate = this.primaryTemplate.bind(this);
        this.isEditDisabled = this.isEditDisabled.bind(this);
        this.isPlanner = this.isPlanner.bind(this);
    }

    private initializeState(): void {
        this.state = {
            filters: {},
            rowData: [],
            crewData: null,
            selectedRow: undefined
        };
    }

    async componentDidMount() {
        await this.loadPageData();
    }

    private isEditDisabled(): boolean {
        const allowedRoles: string[] = ['Booking', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return !access;
    }

    private isNoRowSelected(): boolean {
        return this.state.selectedRow === undefined ||
            this.state.selectedRow.data.id === this.state.crewData?.crew.primaryContact;
    }

    private isPlanner(): boolean {
        const allowedRoles: string[] = ['Booking', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return !access;
    }

    private new_Click(): void {
        this.m_editRef.current!.show(this.editComplete);
    }

    private async makePrimary_Click(): Promise<void> {
        const updateRequest = new UpdateCrewRequest({
            id: this.state.crewData?.crew.id,
            crewName: this.state.crewData?.crew.crewName,
            crewType: this.state.crewData?.crew.crewType,
            workArea: this.state.crewData?.crew.workArea,
            primaryContact: this.state.selectedRow?.data.id
        });
        const result = await this.m_crewClient.callWithReply(CrewDataReply, (client) => client.update(updateRequest));
        result.throwIfFailed();

        await this.loadPageData();
    }

    private async delete_Click(): Promise<void> {
        const deleteResult = await this.m_contactClient.callWithReply(
            ContactDeleteReply, (client) => client.delete(this.state.selectedRow!.data.id));
        deleteResult.throwIfFailed();

        await this.loadPageData();
    }

    private table_onSelectionChange(selection: ContactRow | ContactRow[] | undefined): void {

        this.safeSetState((draft: State) =>
            draft.selectedRow = selection as ContactRow | undefined);

    }

    private async editComplete(newContact: ContactData): Promise<void> {
        const createResult = await this.m_contactClient.callWithReply(
            ContactReply, (client) => client.create(new CreateCrewContactRequest({
                description: newContact.descriptionText,
                contactType: newContact.contactType,
                crewId: this.props.parentId,
                contact: newContact.valueText
        })));
        createResult.throwIfFailed();

        this.safeSetState(draft => (
            draft.rowData.push({data: createResult.reply!.crewContact!}
            )));

        await this.loadPageData();
    }

    private async loadPageData(): Promise<void> {
        const result = await this.m_crewClient.callWithReply(CrewDataReply, (client) => client.get(this.props.parentId));
        result.throwIfFailed();

        //Read contact data
        const contactResult = await this.m_contactClient.callWithReply(
            ContactMultiReply, (client) => client.getAll(this.props.parentId));
        contactResult.throwIfFailed();

        this.safeSetState(draft => {
            draft.rowData = contactResult.reply!.crewContacts.map(n => ({data: n}));
            draft.crewData = result.reply!.crew;
        });
    }

    private primaryTemplate = (rowData: ContactRow) => {
        if(rowData.data.id === this.state.crewData?.crew.primaryContact){
            return 'Primary';
        }

        return '';
    }

    public render(): ReactElement {
        return (
            <React.Fragment>
                <ContactDialog ref={this.m_editRef}
                               contactTypes={this.context.enums?.contactTypes!}
                />
                <MToolbar>
                    <MButton label="Add Contact"
                             icon={MNewIconName}
                             disabled={this.isEditDisabled()}
                             onClick={this.new_Click}/>
                    <MSpacer/>
                    <MButton label="Make Primary"
                             icon={'pi pi-thumbs-up'}
                             color="secondary"
                             disabled={this.isEditDisabled() || this.isNoRowSelected()}
                             onClick={this.makePrimary_Click}/>
                    <MSpacer/>
                    <MButton label="Delete Contact"
                             color="danger"
                             icon={'pi pi-trash'}
                             disabled={this.isEditDisabled() || this.isNoRowSelected()}
                             onClick={this.delete_Click}/>
                </MToolbar>
                <MTable
                    rowKey="data.id"
                    rowData={this.state.rowData}
                    filters={this.state.filters}
                    onSelectionChange={this.table_onSelectionChange}
                    sortField={'data.id'}
                    sortOrder={1}
                >
                    <Column field="data.id"
                            header=""
                            filter={false}
                            sortable={false}
                            body={this.primaryTemplate}
                            style={{width: '10%'}}/>
                    <Column field="data.description"
                            header="Description"
                            filter={false}
                            sortable={true}
                            style={{width: '50%'}}/>
                    {!this.isPlanner() && <Column field="data.contact"
                            header="Contact"
                            filter={false}
                            sortable={false}
                            style={{width: '40%'}}/>}
                </MTable>
            </React.Fragment>
        );
    }
}

export default ContactListControl;